import Close from "@mui/icons-material/Close";
import {
  Badge,
  Box,
  Divider,
  IconButton,
  Paper,
  Portal,
  Slide,
  Stack,
  Tab,
  Tabs,
  Typography,
} from "@mui/material";
import { useEffect, useRef, useState, type FC } from "react";
import { useTranslation } from "react-i18next";

import { useFlag } from "../../server/optimizely";
import { useBrandKit } from "../brand-kit/hooks/use-brand-kit";
import { AiTab } from "./ai-tab";
import { UnsplashTab } from "./unsplash-tab";
import { VectorTab } from "./vector-tab";

type Tab = "unsplash" | "curated" | "ai";

export const UpdateImagerySidebar: FC<{
  open: boolean;
  handleClose: () => void;
  onApplyToAll?: (imageUrl: string) => void;
  onSelectionChange?: (hasSelection: boolean) => void;
  resetKey?: number;
}> = ({ open, handleClose, onApplyToAll, onSelectionChange, resetKey }) => {
  const { t } = useTranslation();
  const [mfai_ai_image_style] = useFlag("mfai_ai_image_style");

  const brandKit = useBrandKit();
  const branding = brandKit?.branding ?? brandKit?.defaultBranding;

  const [tab, setTab] = useState<Tab>("unsplash");
  const [selectedImage, setSelectedImage] = useState<string | null>(null);
  const sidebarRef = useRef<HTMLDivElement>(null);

  const handleImageClick = (url: string): void => {
    setSelectedImage(url);
    onApplyToAll?.(url);
    onSelectionChange?.(true);
  };

  // Clear selection when resetKey changes
  useEffect(() => {
    if (resetKey !== undefined && resetKey > 0) {
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setSelectedImage(null);
      onSelectionChange?.(false);
    }
  }, [resetKey, onSelectionChange]);

  useEffect(() => {
    if (!open) return;

    const onKeyDown = (event: KeyboardEvent): void => {
      if (event.key === "Escape") {
        handleClose();
      }
    };

    const onMouseDown = (event: MouseEvent): void => {
      if (sidebarRef.current && !sidebarRef.current.contains(event.target as Node)) {
        handleClose();
      }
    };

    document.addEventListener("keydown", onKeyDown);
    document.addEventListener("mousedown", onMouseDown);
    return () => {
      document.removeEventListener("keydown", onKeyDown);
      document.removeEventListener("mousedown", onMouseDown);
    };
  }, [handleClose, open]);

  return (
    <Portal>
      <Slide direction="left" in={open} mountOnEnter>
        <Paper
          ref={sidebarRef}
          elevation={12}
          sx={(theme) => ({
            position: "fixed",
            top: 0,
            right: 0,
            height: "100vh",
            width: { xs: "100vw", sm: 420 },
            borderRadius: 0,
            display: "flex",
            flexDirection: "column",
            zIndex: theme.zIndex.drawer + 2,
            borderLeft: `1px solid ${theme.palette.divider}`,
            boxShadow: "0px 10px 50px rgba(15, 23, 42, 0.25)",
            backgroundColor: theme.palette.background.paper,
          })}
        >
          <Stack
            sx={{
              px: 3,
              py: 2.5,
            }}
            gap={2}
          >
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
              }}
            >
              <Typography variant="subtitle1" sx={{ fontWeight: 500 }}>
                {t("Update imagery")}
              </Typography>
              <IconButton
                data-analytics-id="update-imagery-dialog-close"
                aria-label={t("Close")}
                onClick={handleClose}
                size="small"
                sx={{ color: "text.secondary" }}
              >
                <Close data-testid="close-dialog-button" />
              </IconButton>
            </Box>
            <Typography variant="body2" sx={{ color: "text.secondary" }}>
              {t("Replace all imagery (can be undone)")}
            </Typography>
          </Stack>
          <Tabs value={tab} onChange={(_, _tab) => setTab(_tab as Tab)} variant="fullWidth">
            <Tab label={t("Unsplash")} value="unsplash" />
            <Tab label={t("Curated")} value="curated" />
            <Tab
              label={
                <Badge
                  color="secondary"
                  badgeContent="Beta"
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    "& .MuiBadge-badge": {
                      position: "static",
                      transform: "none",
                      marginLeft: 1,
                    },
                  }}
                >
                  {t("AI")}
                </Badge>
              }
              value="ai"
            />
          </Tabs>
          <Divider />
          <UnsplashTab
            onImageClick={handleImageClick}
            selectedImage={selectedImage}
            onSelectionChange={onSelectionChange}
            hidden={tab !== "unsplash"}
          />
          <VectorTab
            onImageClick={handleImageClick}
            selectedImage={selectedImage}
            onSelectionChange={onSelectionChange}
            hidden={tab !== "curated"}
          />
          <AiTab
            onImageClick={handleImageClick}
            selectedImage={selectedImage}
            onSelectionChange={onSelectionChange}
            hidden={tab !== "ai"}
            branding={branding}
            mfai_ai_image_style={mfai_ai_image_style}
          />
        </Paper>
      </Slide>
    </Portal>
  );
};
